import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  CircularProgress,
  Alert,
  Box,
} from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';
import { organizationService } from '../../services/organizationService';
import { Organization, OrganizationInvitation } from '../../types';
import { Vehicle } from '../../types/vehicle';
import { vehicleService } from '../../services/vehicleService';

interface AcceptInvitationProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  email: string;
}

const AcceptInvitation: React.FC<AcceptInvitationProps> = ({ open, onClose, onSuccess, email }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [organizationData, setOrganizationData] = useState<{
    organization: Organization;
    invitation: OrganizationInvitation;
  } | null>(null);
  const { user } = useAuth();

  useEffect(() => {
    const findInvitation = async () => {
      if (!email || !open) return;

      try {
        setLoading(true);
        setError(null);

        // Add a delay to allow Firebase token to fully propagate
        await new Promise(resolve => setTimeout(resolve, 1500));
        
        console.log('Checking for invitations for email:', email);
        
        try {
          const results = await organizationService.findOrganizationsByInvitationEmail(email);
          
          if (results.length === 0) {
            setError('No pending invitations found. If you were invited, please make sure you are using the same email address that received the invitation.');
            return;
          }

          // Take the first invitation found
          const [firstResult] = results;
          console.log('Found invitation:', firstResult);
          setOrganizationData(firstResult);
        } catch (permissionErr) {
          console.error('Permission error finding invitation:', permissionErr);
          
          // If we get a permission error, try a second time after a longer delay
          console.log('Retrying after delay...');
          await new Promise(resolve => setTimeout(resolve, 3000));
          
          try {
            const results = await organizationService.findOrganizationsByInvitationEmail(email);
            
            if (results.length === 0) {
              setError('No pending invitations found. If you were invited, please make sure you are using the same email address that received the invitation.');
              return;
            }

            // Take the first invitation found
            const [firstResult] = results;
            console.log('Found invitation on retry:', firstResult);
            setOrganizationData(firstResult);
          } catch (retryErr) {
            console.error('Error on retry:', retryErr);
            throw retryErr; // Re-throw to be caught by outer catch
          }
        }
      } catch (err) {
        console.error('Error finding invitation:', err);
        if (err instanceof Error && err.message.includes('permission')) {
          setError('Unable to check invitations. Please make sure you are signed in with the email address that received the invitation. You may need to sign out and sign in again.');
        } else {
          setError(err instanceof Error ? err.message : 'Failed to find invitation');
        }
      } finally {
        setLoading(false);
      }
    };

    findInvitation();
  }, [email, open]);

  const handleAccept = async () => {
    if (!user || !organizationData) return;

    try {
      setLoading(true);
      setError(null);

      console.log('Accepting invitation:', organizationData.invitation);
      await organizationService.acceptInvitation(
        organizationData.organization.id,
        organizationData.invitation.id,
        user.id
      );
      
      // Attendre un peu pour laisser le temps à l'assignation de se faire
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Force un rechargement complet pour mettre à jour le rôle et rediriger
      window.location.href = organizationData.invitation.role === 'User' ? '/driver' : '/';
    } catch (err) {
      console.error('Error accepting invitation:', err);
      
      // Provide more specific error messages
      if (err instanceof Error) {
        if (err.message.includes('permission')) {
          console.log('Permission error details:', err);
          setError('Permission error. The security rules have been updated. Please try again in a few moments or refresh the page.');
        } else if (err.message.includes('not found')) {
          setError('Resource not found. Please refresh the page and try again.');
        } else {
          setError(err.message);
        }
      } else {
        setError('Failed to accept invitation');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDecline = async () => {
    if (!organizationData) return;

    try {
      setLoading(true);
      setError(null);

      await organizationService.declineInvitation(
        organizationData.organization.id,
        organizationData.invitation.id
      );
      onClose();
    } catch (err) {
      console.error('Error declining invitation:', err);
      setError(err instanceof Error ? err.message : 'Failed to decline invitation');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Organization Invitation</DialogTitle>
      <DialogContent>
        {loading ? (
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        ) : organizationData ? (
          <Box>
            <Box sx={{ typography: 'body1', mb: 2 }}>
              You have been invited to join {organizationData.organization.name} as a {organizationData.invitation.role.toLowerCase()}.
            </Box>
            <Box sx={{ typography: 'body2', color: 'text.secondary' }}>
              Function: {organizationData.invitation.functionTitle}
            </Box>
            <Box sx={{ typography: 'body2', color: 'text.secondary' }}>
              Invited on: {organizationData.invitation.createdAt.toDate().toLocaleDateString()}
            </Box>
            {organizationData.invitation.vehicleId && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  A vehicle will be assigned to you upon accepting this invitation.
                </Typography>
              </Box>
            )}
          </Box>
        ) : (
          <Box sx={{ typography: 'body1', color: 'text.secondary' }}>
            No pending invitations found for your email address.
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Close
        </Button>
        {organizationData && (
          <>
            <Button onClick={handleDecline} disabled={loading} color="error">
              Decline
            </Button>
            <Button
              onClick={handleAccept}
              disabled={loading}
              variant="contained"
              color="primary"
            >
              Accept
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default AcceptInvitation;
